import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { invitationService } from '../services/invitationService';
import { PublicInvitationResponse } from '../types/invitation';
import { auth } from '../config/firebase';
import { getTosConfig, useTosConfig } from '../utils/termsConfig';


const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';
const PENDING_INVITATION_TOKEN_KEY = 'pendingInvitationToken';

const JoinPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const tosConfig = useTosConfig(); // ToS link target (server-driven version)
  const navigate = useNavigate();
  const { currentUser, signup, loading: authLoading } = useAuth();
  const { refreshShops } = useShop();

  const [invitation, setInvitation] = useState<PublicInvitationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpData, setSignUpData] = useState({ password: '', displayName: '' });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpError, setSignUpError] = useState('');

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const invitationData = await invitationService.getInvitationByToken(token);
        setInvitation(invitationData);
      } catch (err) {
        console.error('Failed to fetch invitation:', err);
        setError('This invitation link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // Native-app hand-off (mirrors the verification email flow via AuthActionPage): on iOS, offer to
  // open the invite in the Carousel Shop AI app via the custom URL scheme. The web accept flow
  // below stays as the fallback for desktop / when the app isn't installed.
  const isIos = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const openInApp = () => {
    if (token) {
      window.location.href = `carouselshopai://join/${token}`;
    }
  };

  useEffect(() => {
    // Auto-attempt once on iOS (iOS shows "Open in Carousel Shop AI?"). Fires on mount; a manual
    // button below covers the case where the user dismisses the prompt.
    if (isIos && token) {
      window.location.href = `carouselshopai://join/${token}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSignIn = () => {
    if (token) {
      sessionStorage.setItem(PENDING_INVITATION_TOKEN_KEY, token);
    }
    navigate('/login');
  };

  const handleAcceptInvitation = async () => {
    if (!token) return;

    setAccepting(true);
    setAcceptError('');

    try {
      await invitationService.acceptInvitation(token);
      sessionStorage.removeItem(PENDING_INVITATION_TOKEN_KEY);
      await refreshShops();
      navigate('/#slides');
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      setAcceptError(err.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invitation?.email) {
      setSignUpError('Invitation email not found');
      return;
    }

    if (signUpData.password.length < 6) {
      setSignUpError('Password must be at least 6 characters');
      return;
    }

    if (signUpData.password !== confirmPassword) {
      setSignUpError('Passwords do not match');
      return;
    }

    if (!agreedToTerms) {
      setSignUpError('You must agree to the Terms of Service.');
      return;
    }

    setSignUpLoading(true);
    setSignUpError('');

    try {
      await signup(invitation.email, signUpData.password, signUpData.displayName || undefined);

      // Record user agreements (blocking — must succeed before invitation acceptance)
      const user = auth.currentUser;
      if (user) {
        const idToken = await user.getIdToken();
        // Record exactly the version shop-svc says is current (and its versioned page), never a
        // hard-coded constant. getTosConfig never rejects; it falls back to the built-in version.
        const tos = await getTosConfig();
        const agreementsResponse = await fetch(
          `${SHOP_API_BASE_URL}/api/v1/user-agreements`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              agreements: [
                {
                  agreementType: 'TERMS_OF_SERVICE',
                  agreementVersion: tos.currentVersion,
                  documentUrl: tos.documentUrl,
                },
              ],
              acceptedVia: 'shop_invitation',
            }),
          }
        );

        if (!agreementsResponse.ok) {
          const errorText = await agreementsResponse.text();
          console.error('Failed to record agreements:', errorText);
          setSignUpError('Failed to record agreement acceptance. Please try again.');
          setSignUpLoading(false);
          return;
        }
      }

      setShowSignUp(false);
    } catch (err: any) {
      console.error('Failed to create account:', err);

      if (err.code === 'auth/email-already-in-use') {
        setSignUpError('An account with this email already exists. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setSignUpError('Password is too weak. Please use at least 6 characters.');
      } else if (err.code === 'auth/invalid-email') {
        setSignUpError('Invalid email address.');
      } else {
        setSignUpError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setSignUpLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const cleanedDateString = dateString.replace(/\[.*?\]$/, '');
      const date = new Date(cleanedDateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getRoleDisplayName = (role: string): string => {
    const roleNames: Record<string, string> = {
      PLATFORM_ADMIN: 'Platform Admin',
      OWNER: 'Owner',
      ADMIN: 'Administrator',
      MEMBER: 'Member',
      VIEWER: 'Viewer',
      CLIENT: 'Client',
      VENDOR: 'Vendor'
    };
    return roleNames[role] || role;
  };

  // Invitation details block used in multiple views
  const renderInvitationDetails = () => (
    <div style={{ marginBottom: '20px' }}>
      <p className="login-subtitle" style={{ marginBottom: '4px' }}>
        <strong>{invitation?.inviterName}</strong> has invited you to join
      </p>
      <h3 style={{ margin: '0 0 12px 0', color: 'var(--text-accent)', fontSize: '20px' }}>
        {invitation?.shopName}
      </h3>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {invitation?.roles.map((role) => (
          <span
            key={role}
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              backgroundColor: 'var(--bg-accent)',
              color: 'var(--primary-dark)',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            {getRoleDisplayName(role)}
          </span>
        ))}
      </div>
    </div>
  );

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            <p className="login-subtitle">Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            <h2>Invalid Invitation</h2>
            <div className="error-message">{error}</div>
            <button className="login-btn" onClick={() => navigate('/')}>
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Non-pending invitation status
  if (invitation && invitation.status !== 'PENDING') {
    const statusMessages: Record<string, { title: string; message: string }> = {
      ACCEPTED: {
        title: 'Invitation Already Accepted',
        message: 'This invitation has already been accepted.'
      },
      EXPIRED: {
        title: 'Invitation Expired',
        message: 'This invitation has expired. Please contact the shop administrator for a new invitation.'
      },
      REVOKED: {
        title: 'Invitation Revoked',
        message: 'This invitation has been revoked by the shop administrator.'
      }
    };

    const statusInfo = statusMessages[invitation.status] || {
      title: 'Invalid Invitation',
      message: 'This invitation is no longer valid.'
    };

    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            <h2>{statusInfo.title}</h2>
            <div className="error-message">{statusInfo.message}</div>
            <button className="login-btn" onClick={() => navigate('/')}>
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not logged in - show sign in / create account
  if (!currentUser) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            <h2>You're Invited!</h2>

            {renderInvitationDetails()}

            <p className="login-subtitle" style={{ fontSize: '12px' }}>
              Expires {invitation && formatDate(invitation.expiresAt)}
            </p>

            {!showSignUp ? (
              <>
                <p className="login-subtitle">
                  Sign in to your account or create a new one to accept this invitation.
                </p>

                <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                  <button className="login-btn" style={{ flex: 1 }} onClick={handleSignIn}>
                    Sign In
                  </button>
                  <button
                    style={{
                      flex: 0,
                      whiteSpace: 'nowrap',
                      padding: '10px 24px',
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '6px',
                      color: 'var(--text-accent)',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowSignUp(true)}
                  >
                    Create Account
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="login-subtitle">
                  Create your account to accept this invitation
                </p>

                {signUpError && <div className="error-message">{signUpError}</div>}

                <form onSubmit={handleCreateAccount}>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={invitation?.email || ''}
                      readOnly
                      disabled
                      style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Display Name (optional)</label>
                    <input
                      type="text"
                      value={signUpData.displayName}
                      onChange={(e) => setSignUpData({ ...signUpData, displayName: e.target.value })}
                      placeholder="Your name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Password *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={signUpData.password}
                        onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                        placeholder="At least 6 characters"
                        required
                        minLength={6}
                        style={{ paddingRight: '50px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '13px',
                          color: 'var(--text-primary)',
                          padding: '4px',
                        }}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Confirm Password *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter your password"
                        required
                        minLength={6}
                        style={{ paddingRight: '50px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '13px',
                          color: 'var(--text-primary)',
                          padding: '4px',
                        }}
                      >
                        {showConfirmPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        style={{ marginTop: '3px', flexShrink: 0 }}
                      />
                      <span>
                        I agree to the{' '}
                        <a href={tosConfig.documentUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-light)' }}>
                          Terms of Service
                        </a>
                      </span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <button
                      type="button"
                      style={{
                        flex: 0,
                        whiteSpace: 'nowrap',
                        padding: '10px 24px',
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '6px',
                        color: 'var(--text-accent)',
                        fontSize: '14px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setShowSignUp(false);
                        setSignUpError('');
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="login-btn"
                      style={{ flex: 1 }}
                      disabled={signUpLoading || !agreedToTerms}
                    >
                      {signUpLoading ? 'Creating Account...' : 'Create Account'}
                    </button>
                  </div>
                </form>
              </>
            )}

            <div className="login-footer">
              <p>Already have an account? <Link to="/login">Log in</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Email mismatch
  const emailMatches = currentUser.email?.toLowerCase() === invitation?.email?.toLowerCase();

  if (!emailMatches) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-form">
            <h2>Email Mismatch</h2>
            <p className="login-subtitle">
              This invitation was sent to <strong>{invitation?.email}</strong>.
            </p>
            <p className="login-subtitle">
              You are logged in as <strong>{currentUser.displayName || currentUser.email}</strong>.
            </p>
            <p className="login-subtitle">
              Please sign in with the correct email address to accept this invitation.
            </p>
            <button className="login-btn" onClick={handleSignIn}>
              Sign In with Different Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Email matches - show accept button
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-form">
          {isIos && (
            <div style={{ marginBottom: '16px', padding: '12px 14px', backgroundColor: 'var(--bg-accent)', border: '1px solid var(--border-light)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '8px' }}>
                Have the Carousel Shop AI app?
              </div>
              <button className="btn btn-primary" onClick={openInApp} style={{ width: '100%' }}>
                Open in the Carousel Shop AI app
              </button>
            </div>
          )}

          <h2>Accept Invitation</h2>

          {renderInvitationDetails()}

          {acceptError && <div className="error-message">{acceptError}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
            <button
              className="btn btn-ghost"
              onClick={() => navigate('/')}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleAcceptInvitation}
              disabled={accepting}
            >
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinPage;
